
import {defineComponent} from 'vue'
import {PageDetailModeEnum} from "@/models/enum/PageDetailModeEnum";
import {PimProductsRestService} from "@/services/rest/pim-pp2/PimProductsRestService";
import {UIStateDto} from "@/dtos/UIStateDto";
import {Pp2ProductDetailRestDtoModel} from "@/models/api/pharma-pim-pp2/Pp2ProductDetailRestDtoModel";
import BaseCard from "@/components/UI/Bootstrap/Card/BaseCard.vue";
import BaseCardHeader from "@/components/UI/Bootstrap/Card/BaseCardHeader.vue";
import BaseCardBody from "@/components/UI/Bootstrap/Card/BaseCardBody.vue";
import MultilingualSwitcher from "@/components/UI/MultilingualSwitcher.vue";
import PimProductsDetailLab from "@/components/layouts/pim/PimProductsDetailLab.vue";
import PimProductsDetailPrices from "@/components/layouts/pim/PimProductsDetailPrices.vue";
import {generateDomId} from "@/helpers/functions/string";
import {UtilityHelper} from "@/helpers/UtilityHelper";
import {arrayHasContent} from "@/helpers/functions/arrays";
import Lightbox from "@/components/UI/Lightbox.vue";
import BaseModal from "@/components/UI/Bootstrap/Modal/BaseModal.vue";
import BaseModalButton from "@/components/UI/Bootstrap/Modal/BaseModalButton.vue";
import PimProductsOverviewLegend from "@/components/layouts/pim/PimProductsOverviewLegend.vue";
import PimProductsOverviewItemIcons from "@/components/layouts/pim/PimProductsOverviewItemIcons.vue";
import PimProductsDetailCategories from "@/components/layouts/pim/PimProductsDetailCategories.vue";
import {FullProductInfoRestDtoModel} from "@/models/api/pharma-pim-mgmt/FullProductInfoRestDtoModel";
import {PimMgmtRestService} from "@/services/rest/pim-mgmt/PimMgmtRestService";
import {NamedTextFragmentRestDtoModel} from "@/models/api/pharma-pim-mgmt/NamedTextFragmentRestDtoModel";
import NamedTextFragmentPanel from "@/components/layouts/pim/mgmt/NamedTextFragmentPanel.vue";
import {EnumClassNamePimEnum} from "@/models/enum/EnumClassNamePimEnum";
import {Tooltip} from "bootstrap";
import {PimFeedbackRestDtoModel} from "@/models/api/pharma-pim-mgmt/PimFeedbackRestDtoModel";
import {AbilityContext} from "@/context/AbilityContext";
import {ProductDetailsCoreRestDtoModel} from "@/models/api/pharma-pim-mgmt/ProductDetailsCoreRestDtoModel";
import {CodeDescriptionRestDto} from "@/api/pharma-cms-content-block/models";
import SelectCategories from "@/components/layouts/catalog/SelectCategories.vue";
import {Config} from "@/models/facade/Config";
import {PimCategorizationRestService} from "@/services/rest/pim-category/PimCategorizationRestService";
import {CnkListRestDto} from "@/api/pharma-pim-category/models";
import {PimCategoryRestService} from "@/services/rest/pim-category/PimCategoryRestService";
import GenericFormWrapper from "@/components/base/GenericFormWrapper.vue";
import {FormMixin} from "@/components/base/FormMixin";
import BaseDeleteButton from "@/components/UI/Bootstrap/BaseDeleteButton.vue";
import {AutoSaveFeedbackStatusEnum} from "@/models/enum/AutoSaveFeedbackStatusEnum";
import AlertError2 from "@/components/UI/Bootstrap/Alert/AlertError2.vue";
import PimFeedbackPanel from "@/components/layouts/pim/mgmt/PimFeedbackPanel.vue";
import BaseSubmittingOverlay from "@/components/UI/Bootstrap/BaseSubmittingOverlay.vue";
import {EnumRestService} from "@/services/rest/enum/EnumRestService";
import {DpModuleEnum} from "@/api/enums/dp-module-enum";

export default defineComponent({
  name: "PimProductsDetail",
  mixins: [FormMixin],
  components: {
    BaseSubmittingOverlay,
    PimFeedbackPanel,
    AlertError2,
    BaseDeleteButton,
    GenericFormWrapper,
    SelectCategories,
    PimProductsDetailCategories,
    PimProductsOverviewItemIcons,
    PimProductsOverviewLegend,
    NamedTextFragmentPanel,
    BaseModalButton,
    BaseModal,
    Lightbox,
    PimProductsDetailPrices,
    PimProductsDetailLab,
    MultilingualSwitcher,
    BaseCardBody,
    BaseCardHeader,
    BaseCard,
  },
  props: {
    cnkCode: {
      type: Number,
      required: true
    }
  },
  data() {
    return {
      catalogCustomerCode: Config.getInstance().getConfig().STARTER_CATALOG_CUSTOMER_CODE as string,

      modalProducerInfoId: generateDomId('modal-producer-info-') as string,
      modalPriceHistoryId: generateDomId('modal-price-history-') as string,

      activeLanguage: 'nl' as string,
      languages: ['nl', 'fr'] as string[],

      mode: PageDetailModeEnum.EDIT,

      product: Pp2ProductDetailRestDtoModel.createWithDefaults(),
      productInfo: FullProductInfoRestDtoModel.createWithDefaults(),
      isEditingNamedTextFragmentWithId: null as string | null,
      isShowFeedbackForFragmentWithId: null as string | null,
      isNewNamedTextFragmentPanelOpen: false,
      isNewTextFragmentPanelOpen: false,
      isShowGeneralFeedback: false,

      isCorePimDetailsEditMode: false,
      corePimDetailsDto: null as ProductDetailsCoreRestDtoModel | null,

      isShowAddDpmanagedCategory: false,
      dpManagedCategoryToAddCode: null as string | null,

      isShowAddInternalCategory: false,
      internalCategoryToAddCode: null as string | null,

      internalProductCategories: [] as CodeDescriptionRestDto[],

      tooltips: [] as Tooltip[],

      trademarkOptionAllValues: [] as CodeDescriptionRestDto[],
      targetGroupOptionAllValues: [] as CodeDescriptionRestDto[],
      unitOptionAllValues: [] as CodeDescriptionRestDto[],

      pimFeedback: PimFeedbackRestDtoModel.createWithDefaults(),
      fragmentId: null as string | null,
      namedFragment: NamedTextFragmentRestDtoModel.createWithDefaults(),

      dpCategoryUI: UIStateDto.createWithDefaults(),
      internalCategoryUI: UIStateDto.createWithDefaults(),
      pimCoreDataUI: UIStateDto.createWithDefaults(),
      pimFeedbackUI: UIStateDto.createWithDefaults(),
      namedFragmentUI: UIStateDto.createWithDefaults(),
    }
  },
  watch: {
    cnkCode() {
      this.changeCNK();
    },
    activeLanguage() {
      this.reloadPimContent();
    }
  },
  mounted() {
    this.performGenericReload(this.initializeAll);
  },
  computed: {
    AbilityContext() {
      return AbilityContext
    },
    UtilityHelper() {
      return UtilityHelper
    },
    hasProductProperties(): boolean {
      return arrayHasContent(this.product.product_properties);
    },
    farmaFrameThumbnailNl(): string {
      return "https://api.farmaframe.be/v1/image/thumbnail/nl/" + this.product.product_info.cnk_code + "/150";
    },
    farmaFrameThumbnailFr(): string {
      return "https://api.farmaframe.be/v1/image/thumbnail/fr/" + this.product.product_info.cnk_code + "/150";
    },
    trademarkOptions(): CodeDescriptionRestDto[] {
      if (this.isCorePimDetailsEditMode) {
        return this.trademarkOptionAllValues;
      }
      return [];
    },
    targetGroupOptions(): CodeDescriptionRestDto[] {
      if (this.isCorePimDetailsEditMode) {
        return this.targetGroupOptionAllValues;
      }
      return [];
    },
    unitOptions(): CodeDescriptionRestDto[] {
      if (this.isCorePimDetailsEditMode) {
        return this.unitOptionAllValues;
      }
      return [];
    },
  },
  methods: {
    async initializeAll(): Promise<void> {
      [
        this.trademarkOptionAllValues,
        this.targetGroupOptionAllValues,
        this.unitOptionAllValues,
        this.internalProductCategories
      ] = await Promise.all([
        EnumRestService.getInstance().getEnumValuesByClassName(EnumClassNamePimEnum.PIM_TRADEMARK, DpModuleEnum.CPC),
        EnumRestService.getInstance().getEnumValuesByClassName(EnumClassNamePimEnum.PIM_TARGET_GROUP, DpModuleEnum.CPC),
        EnumRestService.getInstance().getEnumValuesByClassName(EnumClassNamePimEnum.PIM_UNIT, DpModuleEnum.CPC),
        PimCategoryRestService.getInstance().findInternalProductCategories(null)
      ]);
      await this.reloadPimContent();
      await this.reloadContent();
      this.initializeFormUIStates();
    },
    async changeCNK(): Promise<void> {
      await this.reloadContent();
      this.initializeFormUIStates();
    },
    initializeFormUIStates(): void {
      this.dpCategoryUI.setReady();
      this.dpCategoryUI.setCanSubmit(this.dpManagedCategoryToAddCode !== null);
      this.dpCategoryUI.setError(null);
      this.internalCategoryUI.setReady();
      this.internalCategoryUI.setCanSubmit(this.internalCategoryToAddCode !== null);
      this.internalCategoryUI.setError(null);
      this.pimCoreDataUI.setReady();
      this.pimCoreDataUI.setCanSubmit(this.pimCoreDataUI.getAutoSaveFeedbackStatus() === AutoSaveFeedbackStatusEnum.UNSAVED_CHANGES);
      this.pimCoreDataUI.setError(null);
      this.pimFeedbackUI.setReady();
      this.pimFeedbackUI.setCanSubmit(this.pimFeedbackUI.getAutoSaveFeedbackStatus() === AutoSaveFeedbackStatusEnum.UNSAVED_CHANGES);
      this.pimFeedbackUI.setError(null);
      this.namedFragmentUI.setReady();
      this.namedFragmentUI.setCanSubmit(this.namedFragmentUI.getAutoSaveFeedbackStatus() === AutoSaveFeedbackStatusEnum.UNSAVED_CHANGES);
      this.namedFragmentUI.setError(null);
    },
    async toggleEditCorePimDetails(): Promise<void> {
      if (!this.isCorePimDetailsEditMode) {
        this.corePimDetailsDto = ProductDetailsCoreRestDtoModel.createFromProductDetailsRestDto(this.productInfo?.details);
      }
      this.isCorePimDetailsEditMode = !this.isCorePimDetailsEditMode;
    },
    async removeDPCat(categoryCode: string): Promise<void> {
      await this.performGenericDeleteItemWithKey(categoryCode, this.doRemoveDPCat, this.reloadContent, this.dpCategoryUI as UIStateDto);
    },
    async doRemoveDPCat(categoryCode: string): Promise<void> {
      const payload = {
        cnk_codes: [this.cnkCode],
      } as CnkListRestDto;
      await PimCategorizationRestService.getInstance().removeProductsFromDpCategory(payload, categoryCode);
    },
    async doReload(): Promise<void> {
      this.isCorePimDetailsEditMode = false;
      this.isShowAddInternalCategory = false;
      this.isShowAddDpmanagedCategory = false;
      this.internalCategoryToAddCode = null;
      this.dpManagedCategoryToAddCode = null;

      if (this.mode === PageDetailModeEnum.EDIT) {
        this.product = await PimProductsRestService.getInstance().findPimProductForCnkCode(
          this.cnkCode,
          this.activeLanguage
        );
      }
    },
    prepareAddCategory(categoryCode: string) {
      this.dpManagedCategoryToAddCode = categoryCode;
      this.dpCategoryUI.setCanSubmit(this.dpManagedCategoryToAddCode !== null);
      this.dpCategoryUI.setAutoSaveFeedbackStatus(this.dpManagedCategoryToAddCode === null
        ? AutoSaveFeedbackStatusEnum.NONE
        : AutoSaveFeedbackStatusEnum.UNSAVED_CHANGES);
    },
    async removeIntCat(categoryCode: string): Promise<void> {
      await this.performGenericDeleteItemWithKey(categoryCode, this.doRemoveIntCat, this.reloadContent, this.internalCategoryUI as UIStateDto);
    },
    async doRemoveIntCat(categoryCode: string): Promise<void> {
      const payload = {
        cnk_codes: [this.cnkCode],
      } as CnkListRestDto;
      await PimCategorizationRestService.getInstance().removeProductsFromInternalCategory(payload, categoryCode);
    },
    async submitAddDPCategory(): Promise<void> {
      await this.performGenericSubmit(this.doSubmitAddDPCategory, this.reloadContent, this.dpCategoryUI as UIStateDto);
    },
    async doSubmitAddDPCategory(): Promise<void> {
      await PimCategorizationRestService.getInstance().addDpCategoryToProduct(this.cnkCode, this.dpManagedCategoryToAddCode);
    },
    prepareAddInternalCategory(): void {
      this.internalCategoryUI.setCanSubmit(this.internalCategoryToAddCode !== null);
      this.internalCategoryUI.setAutoSaveFeedbackStatus(this.internalCategoryToAddCode === null
        ? AutoSaveFeedbackStatusEnum.NONE
        : AutoSaveFeedbackStatusEnum.UNSAVED_CHANGES);
    },
    async submitAddInternalCategory(): Promise<void> {
      await this.performGenericSubmit(this.doSubmitAddInternalCategory, this.reloadContent, this.internalCategoryUI as UIStateDto);
    },
    async doSubmitAddInternalCategory(): Promise<void> {
      await PimCategorizationRestService.getInstance().addInternalCategoryToProduct(this.cnkCode, this.internalCategoryToAddCode);
    },
    pimCoreDataChanged(): void {
      this.pimCoreDataUI.setAutoSaveFeedbackStatus(AutoSaveFeedbackStatusEnum.UNSAVED_CHANGES);
      this.pimCoreDataUI.setCanSubmit(true);
    },
    async submitPimCoreData(): Promise<void> {
      await this.performGenericSubmit(this.doSubmitPimCoreData, this.reloadPimContent, this.pimCoreDataUI as UIStateDto);
    },
    async doSubmitPimCoreData(): Promise<void> {
      await PimMgmtRestService.getInstance().updatePimDetailsCore(this.corePimDetailsDto, this.product.product_code);
    },
    async submitNamedFragmentContent(fragmentId: string | null, namedFragment: NamedTextFragmentRestDtoModel): Promise<void> {
      this.fragmentId = fragmentId;
      this.namedFragment = namedFragment;
      await this.performGenericSubmit(this.doSubmitNamedFragmentContent, this.reloadPimContent, this.namedFragmentUI as UIStateDto);
    },
    async doSubmitNamedFragmentContent(): Promise<void> {
      if (this.fragmentId !== null) {
        await PimMgmtRestService.getInstance().updateNamedTextFragment(this.fragmentId, this.namedFragment);
      } else {
        await PimMgmtRestService.getInstance().createNamedTextFragment(this.cnkCode, this.activeLanguage, this.namedFragment);
        this.isNewNamedTextFragmentPanelOpen = false;
      }
    },
    async submitNamedFragmentCancel(fragmentId: string | null): Promise<void> {
      this.isNewNamedTextFragmentPanelOpen = false;
      if (fragmentId !== null) {
        this.toggleIsEditingNamedTextFragmentWithId(fragmentId);
      }
    },
    async submitPimFeedback(feedback: PimFeedbackRestDtoModel): Promise<void> {
      this.pimFeedback = feedback;
      await this.performGenericSubmit(this.doSubmitPimFeedback, this.reloadContent, this.pimFeedbackUI as UIStateDto);
    },
    async doSubmitPimFeedback(): Promise<void> {
      await PimMgmtRestService.getInstance().createPimFeedback(this.cnkCode, this.activeLanguage, this.pimFeedback);
      if (this.pimFeedback.text_fragment_id != null) {
        this.toggleIsShowFeedbackForFragmentWithId(this.pimFeedback.text_fragment_id);
      } else {
        this.isShowGeneralFeedback = false
      }
    },
    async submitPimFeedbackCancel(fragmentId: string | null): Promise<void> {
      this.isShowGeneralFeedback = false
      if (fragmentId !== null) {
        this.toggleIsShowFeedbackForFragmentWithId(fragmentId);
      }
    },
    openNewNamedTextFragmentPanel(): void {
      this.isNewNamedTextFragmentPanelOpen = true;
    },
    toggleIsEditingNamedTextFragmentWithId(fragmentId: string): void {
      this.isShowFeedbackForFragmentWithId = null;
      this.isNewNamedTextFragmentPanelOpen = false;
      if (this.isEditingNamedTextFragmentWithId === fragmentId) {
        this.isEditingNamedTextFragmentWithId = null;
      } else {
        this.isEditingNamedTextFragmentWithId = fragmentId;
      }
    },
    toggleIsShowFeedbackForFragmentWithId(fragmentId: string): void {
      this.isEditingNamedTextFragmentWithId = null;
      this.isNewNamedTextFragmentPanelOpen = false;
      if (this.isShowFeedbackForFragmentWithId === fragmentId) {
        this.isShowFeedbackForFragmentWithId = null;
      } else {
        this.isShowFeedbackForFragmentWithId = fragmentId;
      }
    },
    async deleteNamedTextFragmentWithId(fragmentId: string): Promise<void> {
      this.isEditingNamedTextFragmentWithId = null;
      await PimMgmtRestService.getInstance().deleteNamedTextFragment(fragmentId);
      this.hideTooltips();
    },
    changeLanguage(language: string): void {
      this.activeLanguage = language;
      this.reloadContent();
    },
    initializeTooltips(): void {
      document.querySelectorAll('#pim-products-detail [data-bs-toggle="tooltip"]').forEach(el => {
        const tooltip = new Tooltip(el as Element);
        this.tooltips.push(tooltip);
      });
    },
    hideTooltips(): void {
      this.tooltips.forEach(tt => {
        tt.hide();
      });
    },
    async deleteFragment(fragmentId: string): Promise<void> {
      await this.performGenericDeleteItemWithKey(
        fragmentId,
        this.deleteNamedTextFragmentWithId,
        this.reloadPimContent,
        this.namedFragmentUI as UIStateDto,
        true,
        'cms.confirmDeleteTextFragment'
      );
    },
    async reloadPimContent(): Promise<void> {
      this.productInfo = await PimMgmtRestService.getInstance()
        .findMgmtProductInfo(this.cnkCode, this.activeLanguage);
      this.isEditingNamedTextFragmentWithId = null;
    },
  },
})
